/**
 * Global fetch interceptor — runs once at app boot.
 *
 * Two problems this solves on iOS Safari:
 *  1. Cross-site cookies are dropped between page navigations (ITP).
 *  2. `fetch()` calls in components don't auto-include `credentials: 'include'`.
 *
 * What we do:
 *  - Wrap window.fetch so that for any same-origin OR same-host API call,
 *    we automatically:
 *      a. Set credentials: 'include' (so cookies are sent when available)
 *      b. Inject `Authorization: Bearer <localStorage.ace_admin_token>` if a
 *         mobile unlock token exists. Backend already accepts this header
 *         as an alternative to the cookie.
 *      c. If response is 401 and there's a token, drop it so /m re-prompts.
 *  - Safe to call once at boot; no-op on subsequent imports.
 */

let installed = false;

export function installAuthFetchInterceptor() {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;
  const origFetch = window.fetch.bind(window);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  window.fetch = async (resource, init = {}) => {
    let url = '';
    try {
      url = typeof resource === 'string' ? resource : (resource.url || '');
    } catch { /* */ }

    // Only wrap API calls — not external assets
    const isApi = url.includes(API_BASE) && url.includes('/api/');

    if (isApi) {
      const headers = new Headers(init.headers || (typeof resource !== 'string' ? resource.headers : undefined));
      const token = (() => { try { return localStorage.getItem('ace_admin_token'); } catch { return null; } })();
      if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      init = {
        ...init,
        credentials: init.credentials || 'include',
        headers,
      };
    }

    const res = await origFetch(resource, init);

    if (isApi && res.status === 401) {
      try { localStorage.removeItem('ace_admin_token'); } catch { /* */ }
    }
    return res;
  };
}
